// This month's map tiles, by source, viewed live and downloaded — counted at the
// tile front door (docs/planning/TILE_FRONT_DOOR_PLAN.md). The admin console reads
// the same figures through the same API service.
import { useQuery } from '@tanstack/react-query';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { providerForSource } from '@vortex/basemaps';
import { api } from '../../services/api';

const label = (source: string) => providerForSource(source)?.label ?? source;
const fmt = (n: number) => n.toLocaleString();

export function TileUsageCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['tile-usage'],
    queryFn: () => api.getMyTileUsage(),
    staleTime: 60_000,
  });

  const monthName = data
    ? new Date(`${data.month}-01T00:00:00Z`).toLocaleDateString('en-NZ', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : '';

  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
          <Squares2X2Icon className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-bold text-white">Map tiles this month</h2>
      </div>
      <p className="text-[15px] text-slate-400 mb-5">
        {monthName ? `${monthName}, ` : ''}every chart and satellite tile, viewed live or downloaded for offline use.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data || data.total === 0 ? (
        <p className="text-[15px] text-slate-300 py-2">No map tiles counted this month yet.</p>
      ) : (
        <div className="border border-slate-700 rounded-xl overflow-x-auto">
          <table className="w-full text-[14px] sm:text-[15px]">
            <thead>
              <tr className="bg-slate-900/50 text-slate-400 text-sm">
                <th className="text-left font-medium px-2 sm:px-4 py-3">Source</th>
                <th className="text-right font-medium px-2 sm:px-4 py-3"><span className="sm:hidden">Live</span><span className="hidden sm:inline">Viewed live</span></th>
                <th className="text-right font-medium px-2 sm:px-4 py-3"><span className="sm:hidden">Offline</span><span className="hidden sm:inline">Downloaded</span></th>
                <th className="text-right font-medium px-2 sm:px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.sources.map((s) => (
                <tr key={s.source} className="border-t border-slate-700/60">
                  <td className="px-2 sm:px-4 py-3 text-white">{label(s.source)}</td>
                  <td className="px-2 sm:px-4 py-3 text-right text-slate-200 tabular-nums">{fmt(s.live)}</td>
                  <td className="px-2 sm:px-4 py-3 text-right text-slate-200 tabular-nums">{fmt(s.download)}</td>
                  <td className="px-2 sm:px-4 py-3 text-right text-white tabular-nums">{fmt(s.total)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-600 bg-slate-900/40 font-semibold">
                <td className="px-2 sm:px-4 py-3 text-white">Total</td>
                <td className="px-2 sm:px-4 py-3 text-right text-white tabular-nums">{fmt(data.live)}</td>
                <td className="px-2 sm:px-4 py-3 text-right text-white tabular-nums">{fmt(data.download)}</td>
                <td className="px-2 sm:px-4 py-3 text-right text-cyan-400 tabular-nums">{fmt(data.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
